import { Box, Text } from '@chakra-ui/react';
import { useContext, useEffect } from 'react';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import { getAmountToPay } from '../helpers/amountToPay';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import MerchantContext from '../contexts/MerchantContext';
import StepperContext from '../contexts/StepperContext';
import { getVisiblePaymentMethodsForInvoice } from '../helpers/paymentMethods';
import { invoiceReference } from '../types';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import LineItemGroup from './LineItemGroup';
import PaymentMethodCheckbox from './PaymentMethodICheckbox';
import PalommaPlusBreakdownRow from './PalommaPlusBreakdownRow';
import PaymentSummaryTotalAmount from './PaymentSummaryTotalAmount';

const PaymentMethodSelection = (): JSX.Element => {
  const { invoicesToPay, totalAmountToPay } = useContext(InvoicesToPayContext);
  const { totalSubscriptionAmount } = useContext(SubscriptionsContext);
  const { paymentMethodContext } = useContext(StepperContext);
  const { paymentMethod, setPaymentMethod } = paymentMethodContext;
  const formatCurrency = useCurrencyFormatter();

  const totalToPay = totalAmountToPay + totalSubscriptionAmount;
  const hasSubscriptionAmount = totalSubscriptionAmount > 0;

  const { merchant } = useContext(MerchantContext);
  const { isBlockedForCardPayments, creditsBalance } = useContext(InvoiceDataContext);

  const availableMethods = getVisiblePaymentMethodsForInvoice(
    merchant,
    invoicesToPay,
    totalToPay,
    hasSubscriptionAmount
  );

  useEffect(() => {
    if (!paymentMethod) return;
    const isStillAvailable = availableMethods.some((ele) => ele.id === paymentMethod.id);
    if (!isStillAvailable) {
      setPaymentMethod(undefined);
    }
  }, [availableMethods, paymentMethod, setPaymentMethod]);

  const cardSubtext = '¡Ahora también pago a cuotas!';

  const cashSubtext = 'Paga en efectivo en cualquier Corresponsal Bancario Bancolombia.';

  return (
    <Box width={'100%'}>
      <Box display={'flex'} flexDirection={'column'} py={4} gap={'8px'}>
        <LineItemGroup
          label="Facturas"
          amount={totalAmountToPay}
          items={invoicesToPay.map((ele) => ({
            label: invoiceReference(ele),
            amount: getAmountToPay(ele),
          }))}
        />
        {hasSubscriptionAmount && <PalommaPlusBreakdownRow />}
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          borderTop={'2px solid'}
          borderColor={'#DFDEE2'}
          pt={2}
          mt={1}
        >
          <Text fontWeight={700}>Total a pagar</Text>
          <Text fontWeight={700} fontSize={'1.125rem'}>
            {formatCurrency(totalToPay)}
          </Text>
        </Box>
      </Box>

      {creditsBalance > 0 && <PaymentSummaryTotalAmount />}

      <Box display={'flex'} flexDirection={'column'} width={'100%'} pt={4} gap={2}>
        <Text fontWeight={600}>Métodos de pago</Text>

        {isBlockedForCardPayments && availableMethods.find((ele) => ele.id === 'card')?.active && (
          <Text fontSize={'12px'}>
            Por seguridad, hemos desactivado la opción de pago con tarjeta de crédito. Si crees que esto es un error,
            por favor contáctanos.
          </Text>
        )}

        {availableMethods.map((ele, index) => {
          const tooltipMessage = `Esta factura no permite el pago con ${ele.paymentMethod}. Contáctanos para activarla.`;
          return (
            <PaymentMethodCheckbox
              key={index}
              tooltip={!ele.active ? tooltipMessage : undefined}
              paymentMethod={ele.paymentMethod}
              subtext={ele.id === 'card' ? cardSubtext : ele.id === 'cash' ? cashSubtext : undefined}
              imageUrl={ele.imageUrl}
              isDisabled={(ele.id === 'card' && !!isBlockedForCardPayments) || !ele.active}
              checked={paymentMethod?.id === ele.id}
              onChange={() => setPaymentMethod(ele)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default PaymentMethodSelection;
